import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { handleErrorNotification } from '@utils/error';
import { StatusCodes } from '@constants/enum/common';
import { addNotification } from '@redux/actions/notificationActions';
import { IAPIPaginationData, NotificationType } from '@redux/types/types';
import { createRoleApi, deleteRoleApi, fetchRolesApi, updateRoleApi } from '../api/roles';
import { AsyncActionType } from '../constants/redux';
import { ROLES_PAGE_TABS, RolesMessages } from '../constants/roles';
import { RBACRole } from '../types/roles';

interface RolesState {
  roles: RBACRole[];
  role: RBACRole | undefined;
  fetchRolesStatus: StatusCodes;
  createRoleStatus: StatusCodes;
  updateRoleStatus: StatusCodes;
  rolesPageActiveTab: string;
}

export const initialState: RolesState = {
  roles: [],
  role: undefined,
  fetchRolesStatus: StatusCodes.IDLE,
  createRoleStatus: StatusCodes.IDLE,
  updateRoleStatus: StatusCodes.IDLE,
  rolesPageActiveTab: ROLES_PAGE_TABS.presetRoles.key,
};

export const fetchRoles = createAsyncThunk(
  AsyncActionType.FETCH_ROLES,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchRolesApi<IAPIPaginationData<RBACRole[]>>();
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const createRole = createAsyncThunk(
  AsyncActionType.CREATE_ROLE,
  async (payload: RBACRole, { dispatch, rejectWithValue }) => {
    try {
      const response = await createRoleApi(payload);
      dispatch(
        addNotification({
          content: RolesMessages.CREATE_ROLE_SUCCESS,
          type: NotificationType.Success,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const updateRole = createAsyncThunk(
  AsyncActionType.UPDATE_ROLE,
  async (payload: RBACRole, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateRoleApi(payload);
      dispatch(
        addNotification({
          content: RolesMessages.UPDATE_ROLE_SUCCESS,
          type: NotificationType.Success,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const deleteRole = createAsyncThunk(
  AsyncActionType.DELETE_ROLE,
  async (payload: RBACRole, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteRoleApi(payload);
      dispatch(
        addNotification({
          content: RolesMessages.DELETE_ROLE_SUCCESS,
          type: NotificationType.Success,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    setCurrentRole: (state, action: PayloadAction<RBACRole | undefined>) => {
      state.role = action.payload;
    },
    setRolesPageActiveTab: (state, action: PayloadAction<string>) => {
      state.rolesPageActiveTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.fetchRolesStatus = StatusCodes.IN_PROGRESS;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.roles = action.payload.data;
        state.fetchRolesStatus = StatusCodes.SUCCESS;
      })
      .addCase(fetchRoles.rejected, (state) => {
        state.fetchRolesStatus = StatusCodes.ERROR;
      });
    builder
      .addCase(createRole.pending, (state) => {
        state.createRoleStatus = StatusCodes.IN_PROGRESS;
      })
      .addCase(createRole.fulfilled, (state) => {
        state.createRoleStatus = StatusCodes.SUCCESS;
      })
      .addCase(createRole.rejected, (state) => {
        state.createRoleStatus = StatusCodes.ERROR;
      });
    builder
      .addCase(updateRole.pending, (state) => {
        state.updateRoleStatus = StatusCodes.IN_PROGRESS;
      })
      .addCase(updateRole.fulfilled, (state) => {
        state.updateRoleStatus = StatusCodes.SUCCESS;
      })
      .addCase(updateRole.rejected, (state) => {
        state.updateRoleStatus = StatusCodes.ERROR;
      });
  },
});

export const { setCurrentRole, setRolesPageActiveTab } = rolesSlice.actions;

export default rolesSlice.reducer;
